import React, { useState } from "react";
import MoleculeForm from "./MoleculeForm.js";
import StatsBox from "../StatsPage/StatsBox.js";
import CreatableSelect from 'react-select/creatable';
import SubmitInputField from "./submitInputField.js";
import ResultInfoText from "../ResultsTable/ResultInfoText.js";
import SubmitCreatableSelect from "./SubmitCreatableSelect.js";
import CopyMechanismDialog from "./CopyMechanismDialog.js";
import getMechanismWarnings from "../../functions/mechanismWarnings.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import axios from "../../api/axios.js";

/* Arrow + step-type picker rendered between two molecules */
function StepArrow({ mechanismTypes, value, onChange, possibleNext }) {
  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 140,
      padding: "0 4px",
      alignSelf: "stretch",
    }}>
      {possibleNext && possibleNext.length > 0 && (
        <div style={{ fontSize: 10, color: "#777", textAlign: "center", marginBottom: 4, maxWidth: 160, lineHeight: 1.2 }}>
          {possibleNext.map(item => `${item.type}: ${item.name}`).join(", ")}
        </div>
      )}
      <div style={{ fontSize: 20, color: "#999", lineHeight: 1 }}>→</div>
      <div style={{ minWidth: 150, marginTop: 4 }}>
        <CreatableSelect
          isClearable
          options={mechanismTypes}
          value={value ? { value, label: value } : null}
          onChange={(e) => onChange(e ? e.value : "")}
          placeholder="Step type..."
          styles={{
            control: (base) => ({ ...base, minHeight: 30, fontSize: 12 }),
          }}
        />
      </div>
      <div style={{ fontSize: 20, color: "#999", lineHeight: 1, marginTop: 2 }}>→</div>
    </div>
  );
}

const MechanismFormGrid = ({
  substrate,
  product,
  onChange,
  mechanismTypes,
  evidenceTypes,
  final_step_change,
  sessionSources = [],
  mechanismMeta = {},
  onCopyMechanism = null,
}) => {
  const [mechanismSteps, setMechanismSteps] = useState([]);
  const [evidence, setEvidence] = useState("");
  const [finalStepType, setFinalStepType] = useState("");
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);

  const addStep = () => {
    const newSteps = [
      ...mechanismSteps,
      { molecule: { stereochemistry: "uncertain" }, mechanism: "", evidence: "", possible_next: [] },
    ];
    setMechanismSteps(newSteps);
    onChange(newSteps);
  };

  const removeLastStep = () => {
    if (mechanismSteps.length === 0) return;
    const newSteps = mechanismSteps.slice(0, -1);
    setMechanismSteps(newSteps);
    onChange(newSteps);
  };

  /*
   * Steps handed over by the "Copy mechanism" dialog replace whatever is defined
   * here. `extras` may also carry the evidence/publication of the copied source;
   * those live on the reaction, so they have to reach it in the same update as the
   * steps - one call per field would each start from the same stale reaction and
   * only the last one would survive.
   */
  const applyCopiedMechanism = (steps, extras = {}) => {
    setMechanismSteps(steps);
    if (extras.evidence) setEvidence(extras.evidence);
    if (onCopyMechanism) {
      onCopyMechanism(steps, extras);
    } else {
      onChange(steps);
    }
  };

  async function checkPossibleNext(martsId) {
    if (!/^marts_[ERM][0-9]{5}$/.test(martsId)) return [];
    try {
      const response = await axios.post("/submitcheck", { requestType: "possibleMechSteps", marts: martsId });
      if (response.status === 200 && response.data !== "No mechanism steps found") {
        return response.data.map((item) => ({ type: item.type, marts: item.marts_id, name: item.name }));
      }
    } catch { /* ignore */ }
    return [];
  }

  const updateStepMolecule = async (index, updatedMolecule) => {
    const updatedSteps = [...mechanismSteps];
    updatedSteps[index] = { ...updatedSteps[index], molecule: updatedMolecule };
    if (updatedMolecule.marts) {
      const possibleSteps = await checkPossibleNext(updatedMolecule.marts);
      if (index + 1 < updatedSteps.length) {
        updatedSteps[index + 1] = { ...updatedSteps[index + 1], possible_next: possibleSteps };
      }
    }
    setMechanismSteps(updatedSteps);
    onChange(updatedSteps);
  };

  const updateStepType = (index, value) => {
    const updatedSteps = [...mechanismSteps];
    updatedSteps[index] = { ...updatedSteps[index], mechanism: value };
    setMechanismSteps(updatedSteps);
    onChange(updatedSteps);
  };

  const updateFinalStepType = (value) => {
    setFinalStepType(value);
    final_step_change("final_mech_step", value);
  };

  const handleEvidenceChange = (e) => {
    const val = e?.e ? e.e.value || "" : (e?.target?.value || "");
    setEvidence(val);
    final_step_change("final_mech_evidence", val);
  };

  /*
   * Build a flat sequence of items to render in a flex-wrap row:
   *   [Substrate] →step→ [Int1] →step→ [Int2] →step→ ... →step→ [Product]
   * Each molecule and each arrow is a flex item. The row wraps naturally.
   * When it wraps, a dashed separator line spans the full width.
   */
  const items = [];

  // Substrate
  items.push({ type: "molecule", key: "substrate", render: (
    <MoleculeForm molecule={substrate} onChange={() => {}} title_text="Substrate" disabled={true} moleculeId_start={substrate.molecule_id} backgroundColor="#eef1ee" />
  )});

  // Intermediates with arrows before each
  mechanismSteps.forEach((step, i) => {
    items.push({ type: "arrow", key: `arrow-${i}`, render: (
      <StepArrow
        mechanismTypes={mechanismTypes}
        value={step.mechanism}
        onChange={(val) => updateStepType(i, val)}
        possibleNext={step.possible_next}
      />
    )});
    items.push({ type: "molecule", key: `int-${i}`, render: (
      <MoleculeForm
        title_text={`Intermediate ${i + 1}`}
        molecule={step.molecule}
        onChange={(updatedMol) => updateStepMolecule(i, updatedMol)}
        moleculeId_start={step.molecule.molecule_id}
        backgroundColor="#e0e3e0"
      />
    )});
  });

  // Chemistry sanity checks, recomputed from what is currently in the form.
  const warnings = getMechanismWarnings(substrate, mechanismSteps, product, finalStepType);

  // Final arrow + product
  items.push({ type: "arrow", key: "arrow-final", render: (
    <StepArrow
      mechanismTypes={mechanismTypes}
      value={finalStepType}
      onChange={updateFinalStepType}
      possibleNext={[]}
    />
  )});
  items.push({ type: "molecule", key: "product", render: (
    <MoleculeForm molecule={product} onChange={() => {}} title_text="Product" disabled={true} moleculeId_start={product.molecule_id} backgroundColor="#eef1ee" />
  )});

  return (
    <StatsBox
      title={<ResultInfoText text="Mechanism" row={1} fontSize="24px" buttonClass="plane-text-button-no-background" />}
      backgroundColor="RGB(245,245,245)"
    >
      {/* Main flow grid */}
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "4px 0",
        rowGap: "12px",
        position: "relative",
      }}>
        {items.map((item) => (
          <div key={item.key} style={{
            flex: item.type === "molecule" ? "1 1 280px" : "0 0 auto",
            maxWidth: item.type === "molecule" ? "420px" : undefined,
          }}>
            {item.render}
          </div>
        ))}
      </div>

      {/* Separator */}
      <div style={{ borderTop: "1px solid #ccc", margin: "14px 0" }} />

      {/* Checks sit below the flow, next to the evidence and publication fields:
          on a long mechanism the top of the form is scrolled far out of sight. */}
      {warnings.length > 0 && (
        <div className="mechanism-warning">
          <div className="mechanism-warning-title">
            <FontAwesomeIcon icon={faExclamationCircle} /> Please check the mechanism
          </div>
          <ul>
            {warnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Evidence for the whole mechanism */}
      <SubmitCreatableSelect
        label="Evidence"
        options={evidenceTypes}
        onChange={handleEvidenceChange}
        placeholder="Evidence..."
        target="evidence"
        value={evidence ? { value: evidence, label: evidence } : { value: "", label: "" }}
      />

      <SubmitInputField name="publication" value={mechanismMeta.publication || ""} onChange={(e) => final_step_change("mechanism_publication", e.target.value)} label="Publication link" />
      <SubmitInputField name="publicationdoi" value={mechanismMeta.doi || ""} onChange={(e) => final_step_change("mechanism_doi", e.target.value)} label="Publication DOI" />

      <div style={{ marginTop: 8 }}>
        <button className="basic-button" onClick={addStep}>Add Step</button>
        <button className="basic-button" onClick={removeLastStep} disabled={mechanismSteps.length === 0}>Remove Step</button>
        <button className="basic-button" onClick={() => setCopyDialogOpen(true)}>Copy Mechanism</button>
      </div>

      {copyDialogOpen && (
        <CopyMechanismDialog
          substrate={substrate}
          sessionSources={sessionSources}
          onApply={applyCopiedMechanism}
          onClose={() => setCopyDialogOpen(false)}
        />
      )}
    </StatsBox>
  );
};

export default MechanismFormGrid;
